import { useState, useEffect } from 'react';
import { bestProducts as allProducts } from '../../mocks/products';
import Navbar from '../../components/feature/Navbar';
import PageFooter from '../../components/feature/PageFooter';
import ProductCard from './components/ProductCard';
import ProductModal from './components/ProductModal';
import { injectJsonLd, setDocumentTitle, setMetaDescription, setCanonical } from '../../utils/seo';

type SortOption = 'popular' | 'price-low' | 'price-high' | 'rating';
type CategoryFilter = 'all' | 'spirulina' | 'yaksonkong';

export default function ProductsPage() {
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('all');
  const [sortBy, setSortBy] = useState<SortOption>('popular');
  const [selectedProduct, setSelectedProduct] = useState<typeof allProducts[0] | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://jayeondameun.kr';

    setDocumentTitle('제품 목록 | 자연담은 스피루리나 약손콩 건강식품');
    setMetaDescription(
      '자연담은의 프리미엄 스피루리나 파우더, 정제, 캡슐, 약손콩 분말, 환 등 유기농 건강식품 전체 제품을 만나보세요. 국내 유기농 인증, HACCP 시설 생산.',
    );
    setCanonical(`${siteUrl}/products`);

    const itemListElements = allProducts.map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Product',
        name: product.name,
        description: product.description,
        url: `${siteUrl}/products`,
        offers: {
          '@type': 'Offer',
          price: product.price,
          priceCurrency: 'KRW',
          availability:
            product.stock > 0
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: product.rating,
          reviewCount: product.reviews,
          bestRating: 5,
          worstRating: 1,
        },
      },
    }));

    const cleanup = injectJsonLd('jsonld-products', [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '자연담은 건강식품 제품 목록',
        url: `${siteUrl}/products`,
        description:
          '프리미엄 스피루리나와 약손콩 건강식품 전체 제품 목록. 유기농 스피루리나 파우더, 정제, 캡슐, 약손콩 분말, 환 등',
        inLanguage: 'ko-KR',
        isPartOf: { '@type': 'WebSite', url: `${siteUrl}/`, name: '자연담은' },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '홈', item: `${siteUrl}/` },
            { '@type': 'ListItem', position: 2, name: '제품', item: `${siteUrl}/products` },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: '자연담은 전체 제품',
        description: '유기농 스피루리나, 약손콩 건강식품 전체 목록',
        numberOfItems: allProducts.length,
        itemListElement: itemListElements,
      },
    ]);

    return cleanup;
  }, []);

  useEffect(() => {
    setLoaded(true);
    window.scrollTo(0, 0);
  }, []);

  const categories = [
    { id: 'all' as CategoryFilter, label: '전체', icon: 'ri-apps-line' },
    { id: 'spirulina' as CategoryFilter, label: '스피루리나', icon: 'ri-leaf-line' },
    { id: 'yaksonkong' as CategoryFilter, label: '약손콩', icon: 'ri-seedling-line' },
  ];

  const sortOptions = [
    { id: 'popular' as SortOption, label: '인기순' },
    { id: 'price-low' as SortOption, label: '낮은 가격순' },
    { id: 'price-high' as SortOption, label: '높은 가격순' },
    { id: 'rating' as SortOption, label: '평점순' },
  ];

  const filteredProducts = allProducts.filter(product => 
    selectedCategory === 'all' || product.category === selectedCategory
  );

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'rating':
        return b.rating - a.rating;
      case 'popular':
      default:
        return b.reviews - a.reviews;
    }
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/30 via-white to-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-10 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-8 md:mb-12 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Noto Serif KR, serif' }}>
              건강한 선택, 프리미엄 제품
            </h1>
            <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto px-2">
              자연에서 온 최고의 영양, 콩이사랑의 엄선된 제품을 만나보세요
            </p>
          </div>

          {/* Category Filter */}
          <div className={`flex flex-wrap justify-center gap-2 md:gap-3 mb-6 md:mb-8 transition-all duration-700 delay-100 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 md:px-6 py-2 md:py-3 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                  selectedCategory === category.id
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/30'
                    : 'bg-white text-gray-700 hover:bg-emerald-50 border border-gray-200'
                }`}
              >
                <i className={`${category.icon} text-base w-4 h-4 flex items-center justify-center`}></i>
                {category.label}
              </button>
            ))}
          </div>

          {/* Sort & Count */}
          <div className={`flex flex-row justify-between items-center gap-3 mb-6 md:mb-8 transition-all duration-700 delay-200 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-xs md:text-sm text-gray-600">
              총 <strong className="text-emerald-600 font-semibold">{sortedProducts.length}</strong>개의 제품
            </p>
            <div className="flex items-center gap-2">
              <label htmlFor="sort" className="text-xs md:text-sm text-gray-600 whitespace-nowrap">정렬:</label>
              <select
                id="sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="px-2 md:px-4 py-1.5 md:py-2 rounded-lg border border-gray-200 text-xs md:text-sm font-medium text-gray-700 bg-white cursor-pointer hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all"
              >
                {sortOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6">
            {sortedProducts.map((product, index) => (
              <ProductCard
                key={product.id}
                product={product}
                index={index}
                loaded={loaded}
                onViewDetails={() => setSelectedProduct(product)}
              />
            ))}
          </div>

          {sortedProducts.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4 w-16 h-16 flex items-center justify-center mx-auto"></i>
              <p className="text-gray-500 text-lg">해당 카테고리에 제품이 없습니다.</p>
            </div>
          )}
        </div>
      </section>

      <PageFooter />

      {/* Product Modal */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
}